import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useDeals } from '../../../store/DealsContext';
import { calculateComparableMetrics } from '../../../utils/calculations';
import { formatCurrency } from '../../../utils/formatters';
import { useModelExport } from '../../../hooks/useModelExport';

interface ComparableAnalysisProps {
  onClose: () => void;
}

export default function ComparableAnalysis({ onClose }: ComparableAnalysisProps) {
  const { deals } = useDeals();
  const { exportToExcel, exporting } = useModelExport();
  const [selectedDeal, setSelectedDeal] = useState('');
  const [comparables, setComparables] = useState<any[]>([]);
  const [results, setResults] = useState<any>(null);

  const handleAddComparable = (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    
    setComparables([...comparables, {
      name: formData.get('name'),
      evRevenue: formData.get('evRevenue'),
      evEbitda: formData.get('evEbitda')
    }]);
    
    form.reset();
  };

  const handleCalculate = () => {
    if (comparables.length === 0) return;

    const metrics = calculateComparableMetrics(comparables);
    const deal = deals.find(d => d.id === selectedDeal);
    
    if (!deal) return;

    setResults({
      impliedValueRevenue: deal.targetCompany.revenue * metrics.medianEvRevenue,
      impliedValueEbitda: (deal.targetCompany.revenue * 0.15) * metrics.medianEvEbitda,
      recommendedRange: {
        low: deal.value * 0.8,
        high: deal.value * 1.2
      }
    });
  };

  const handleExport = () => {
    if (!results) return;
    exportToExcel({
      comparables,
      results
    }, 'comparable-analysis.xlsx');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Comparable Analysis</h3>
          <button onClick={onClose}>
            <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
          </button>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Deal</label>
                <select
                  value={selectedDeal}
                  onChange={(e) => setSelectedDeal(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="">Select Deal</option>
                  {deals.map((deal) => (
                    <option key={deal.id} value={deal.id}>{deal.name}</option>
                  ))}
                </select>
              </div>

              <form onSubmit={handleAddComparable} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Company Name</label>
                  <input
                    name="name"
                    type="text"
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">EV/Revenue</label>
                  <input
                    name="evRevenue"
                    type="number"
                    step="0.1"
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">EV/EBITDA</label>
                  <input
                    name="evEbitda"
                    type="number"
                    step="0.1"
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                >
                  Add Comparable
                </button>
              </form>

              {comparables.length > 0 && (
                <button
                  onClick={handleCalculate}
                  disabled={!selectedDeal}
                  className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  Calculate
                </button>
              )}
            </div>

            {results && (
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Results</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Implied Value (Revenue)</span>
                    <span className="text-sm font-medium text-gray-900">
                      {formatCurrency(results.impliedValueRevenue)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Implied Value (EBITDA)</span>
                    <span className="text-sm font-medium text-gray-900">
                      {formatCurrency(results.impliedValueEbitda)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Recommended Range</span>
                    <span className="text-sm font-medium text-gray-900">
                      {formatCurrency(results.recommendedRange.low)} - {formatCurrency(results.recommendedRange.high)}
                    </span>
                  </div>
                </div>

                <button
                  onClick={handleExport}
                  disabled={exporting}
                  className="w-full px-4 py-2 text-sm font-medium text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50"
                >
                  {exporting ? 'Exporting...' : 'Export to Excel'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}